import type { Job, Issue } from "@/data/types";
import { getStageConfig, getNextStages } from "@/data/workflowEngine";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { deriveFinancialClearance } from "@/data/financialClearance";
import { migrationBypasses } from "@/data/import/migrationVisibilityGuard";

/**
 * SuggestedAction — single source of truth for "what should I do next on this job?"
 *
 * Action-First UX rule (applied across Job Detail, Jobs table, Maverick, etc.):
 *   - `cta` is the imperative verb users see on the button ("Generate PO", "Call Customer").
 *   - `executionMode` tells consumers how to handle the click:
 *       "modal"     → open a pre-filled modal in place (e.g. OrderingModal)
 *       "direct"    → mutate state immediately + toast (e.g. mark resolved)
 *       "navigate"  → only when full module context is required (legacy default)
 *
 * Consumers that don't recognise these new fields fall back to the existing
 * `link` (navigate) behavior — fully backward compatible.
 */
export type ActionExecutionMode = "direct" | "modal" | "navigate";

export interface SuggestedAction {
  id?: string;
  label: string;
  description: string;
  priority: "high" | "medium" | "low";
  link?: string;
  icon: "crew" | "materials" | "permit" | "schedule" | "issue" | "dispatch" | "qa" | "closeout" | "review";
  /** Imperative verb shown on the action button. Defaults to `label` if omitted. */
  cta?: string;
  /** How consumers should execute this action. Defaults to "navigate" if omitted. */
  executionMode?: ActionExecutionMode;
}

export function getJobSuggestedActions(job: Job, openIssues: Issue[]): SuggestedAction[] {
  const actions: SuggestedAction[] = [];
  const permittingBypassed = migrationBypasses(job, "permitting");
  const orderingBypassed = migrationBypasses(job, "ordering");
  const buildReviewBypassed = migrationBypasses(job, "build_review");

  // Sequential flow: Permitting → Build Review → Ordering
  // Each CTA tier only appears after the upstream tier is done or bypassed.
  const permittingCleared = permittingBypassed || !job.permitRequired || job.permitStatus === "Approved";
  const buildReviewCleared = buildReviewBypassed || !!job.approvedForOrdering;

  // Keep completed jobs in the action engine until formal closeout is done.
  // A job can be operationally "Completed" while still pending final closeout.
  if (job.currentStage === "Completed" && job.closeoutStatus === "Closed") return actions;

  // ── Pre-spend gate: anything that obligates company money/labor (ordering,
  // permit submission, crew assignment, scheduling) is suppressed when the
  // gate is not cleared. We surface a single "Complete Project Review" CTA
  // instead so operators can't accidentally trigger spend before approval.
  const gate = derivePreSpendGate(job);
  if (!gate.cleared) {
    const blocker = gate.primaryBlocker?.name;
    const gateAction: SuggestedAction =
      blocker === "rescission"
        ? {
            label: "Rescission Cooling-Off Active",
            description: gate.primaryBlocker?.detail || gate.shortReason,
            priority: "high",
            icon: "schedule",
            cta: "Review Job Timeline",
            link: `/jobs/${job.id}`,
            executionMode: "navigate",
          }
        : blocker === "financial"
          ? {
              label: "Resolve Financial Clearance",
              description: gate.primaryBlocker?.detail || gate.shortReason,
              priority: "high",
              icon: "closeout",
              cta: "Open Financials",
              link: `/jobs/${job.id}?tab=financials`,
              executionMode: "navigate",
            }
          : {
              label: "Complete Project Review",
              description: gate.shortReason,
              priority: "high",
              icon: "review",
              cta: "Resolve Project Review",
              link: "/intake-review",
              executionMode: "navigate",
            };
    actions.push({
      ...gateAction,
    });

    // Still allow non-spend actions (issues, QA, closeout) — those don't cost money.
    if (openIssues.length > 0) {
      const urgent = openIssues.filter((i) => i.severity === "Urgent");
      actions.push({
        label: `Resolve ${openIssues.length} Open Issue${openIssues.length > 1 ? "s" : ""}`,
        description: urgent.length > 0
          ? `${urgent.length} urgent issue${urgent.length > 1 ? "s" : ""} require immediate attention.`
          : "Address open issues to improve job health.",
        priority: urgent.length > 0 ? "high" : "medium",
        link: "/issues",
        icon: "issue",
        cta: "Resolve Issue",
        executionMode: "modal",
      });
    }

    return actions.sort((a, b) => {
      const p = { high: 0, medium: 1, low: 2 };
      return p[a.priority] - p[b.priority];
    });
  }

  // Assignment
  if (job.assignmentType === "Unassigned") {
    actions.push({
      label: "Assign Crew or Subcontractor",
      description: "This job has no crew or subcontractor assigned yet.",
      priority: "high",
      icon: "crew",
      cta: "Assign Crew",
      link: "/scheduling",
      executionMode: "navigate", // Crew picker lives in scheduling — navigation justified
    });
  }

  // Permit
  if (!permittingBypassed && job.permitRequired && job.permitStatus === "Revision Needed") {
    actions.push({
      label: "Address Permit Revision",
      description: "Permit requires revision before proceeding.",
      priority: "high",
      link: "/permitting",
      icon: "permit",
      cta: "Open Permit Tracker",
      executionMode: "navigate", // Full permit detail context required
    });
  } else if (!permittingBypassed && job.permitRequired && job.permitStatus === "Submitted") {
    actions.push({
      label: "Follow Up on Permit",
      description: "Permit has been submitted and is awaiting approval.",
      priority: "medium",
      link: "/permitting",
      icon: "permit",
      cta: "Open Permit Tracker",
      executionMode: "navigate",
    });
  }

  // Materials — modal-eligible (OrderingModal accepts a Job)
  // Sequential gate: permitting and build review must be done (or bypassed) before ordering CTAs appear.
  if (!orderingBypassed && permittingCleared && buildReviewCleared && job.materialReadiness === "Missing Items") {
    actions.push({
      label: "Resolve Missing Materials",
      description: `Missing items need to be ordered or sourced. ${job.missingItemsList || ""}`.trim(),
      priority: "high",
      link: "/warehouse",
      icon: "materials",
      cta: "Generate PO",
      executionMode: "modal", // Consumers should open OrderingModal pre-filled with this job
    });
  } else if (!orderingBypassed && permittingCleared && buildReviewCleared && job.orderStatus === "Not Ordered") {
    actions.push({
      label: "Order Materials",
      description: "Materials have not been ordered for this job.",
      priority: "high",
      link: "/ordering-queue",
      icon: "materials",
      cta: "Generate PO",
      executionMode: "modal",
    });
  } else if (!orderingBypassed && permittingCleared && buildReviewCleared && (job.materialReadiness === "Ordered" || job.materialReadiness === "Not Ordered")) {
    actions.push({
      label: "Track Material Delivery",
      description: "Materials are ordered but not yet received.",
      priority: "medium",
      link: "/warehouse",
      icon: "materials",
      cta: "Track Delivery",
      executionMode: "navigate", // Receiving lives in warehouse module
    });
  }

  // Scheduling — modal-eligible (RescheduleModal accepts a Job)
  if (!job.scheduledInstallDate && ["Ready for Scheduling", "Scheduled Tentative"].includes(job.currentStage)) {
    actions.push({
      label: "Schedule Installation",
      description: "Job is ready to be scheduled for installation.",
      priority: "high",
      link: "/scheduling",
      icon: "schedule",
      cta: "Schedule Job",
      executionMode: "modal", // Quick reschedule modal opens in place
    });
  }

  // Dispatch — modal-eligible (QuickAssignModal picks top contractor)
  if (job.currentStage === "Ready for Dispatch" || job.dispatchStatus === "Ready for Dispatch") {
    actions.push({
      label: "Dispatch Job",
      description: "Job is ready to be dispatched to a crew.",
      priority: "high",
      link: "/scheduling",
      icon: "dispatch",
      cta: "Quick Assign",
      executionMode: "modal",
    });
  } else if (job.dispatchStatus === "Rejected") {
    actions.push({
      label: "Reassign After Rejection",
      description: "Contractor rejected this job. Reassign to another crew.",
      priority: "high",
      icon: "dispatch",
      link: "/scheduling",
      cta: "Quick Assign",
      executionMode: "modal",
    });
  }

  // Open issues — modal-eligible (QuickResolveIssueModal opens for top issue)
  if (openIssues.length > 0) {
    const urgent = openIssues.filter((i) => i.severity === "Urgent");
    actions.push({
      label: `Resolve ${openIssues.length} Open Issue${openIssues.length > 1 ? "s" : ""}`,
      description: urgent.length > 0
        ? `${urgent.length} urgent issue${urgent.length > 1 ? "s" : ""} require immediate attention.`
        : "Address open issues to improve job health.",
      priority: urgent.length > 0 ? "high" : "medium",
      link: "/issues",
      icon: "issue",
      cta: "Resolve Issue",
      executionMode: "modal",
    });
  }

  // QA
  if (!job.qaSubmitted && (job.currentStage === "In Progress" || job.currentStage === "QA Review" || job.currentStage === "Completed")) {
    actions.push({
      label: "Submit QA Documentation",
      description: "Install QA form has not been submitted yet.",
      priority: job.currentStage === "QA Review" ? "high" : "medium",
      link: `/qa/${job.id}`,
      icon: "qa",
      cta: "Start QA",
      executionMode: "navigate", // QA form is multi-step
    });
  }

  const postInstallFinancial = deriveFinancialClearance(job, "post_install");
  if (
    job.closeoutStatus !== "Closed" &&
    job.qaSubmitted === true &&
    postInstallFinancial.status === "cleared"
  ) {
    actions.push({
      label: "Complete Project Closeout",
      description: "Final payment is settled. Finalize closeout documentation and sign-off.",
      priority: "high",
      link: `/closeout?job=${encodeURIComponent(job.id)}`,
      icon: "closeout",
      cta: "Open Closeout",
      executionMode: "navigate",
    });
  }

  return actions.sort((a, b) => {
    const p = { high: 0, medium: 1, low: 2 };
    return p[a.priority] - p[b.priority];
  });
}

export function getIssueSuggestedActions(issue: Issue, job: Job | undefined): SuggestedAction[] {
  const actions: SuggestedAction[] = [];
  let idx = 0;

  if (issue.status === "Open") {
    actions.push({
      id: `start-investigation-${idx++}`,
      label: "Start Investigation",
      description: "Move issue to In Progress to begin resolution.",
      priority: "high",
      icon: "issue",
    });
  }

  if (issue.status === "In Progress") {
    actions.push({
      id: `mark-resolved-${idx++}`,
      label: "Mark as Resolved",
      description: "If the issue has been addressed, mark it resolved.",
      priority: "medium",
      icon: "issue",
    });
  }

  if (issue.status === "Resolved") {
    actions.push({
      id: `close-issue-${idx++}`,
      label: "Close Issue",
      description: "Verify resolution and close the issue.",
      priority: "medium",
      icon: "issue",
    });
  }

  if (issue.issueType === "Material Issue" || issue.issueType === "Ordering Issue") {
    actions.push({
      id: `warehouse-status-${idx++}`,
      label: "Check Warehouse Status",
      description: "Verify material availability and ordering status.",
      priority: "medium",
      link: "/warehouse",
      icon: "materials",
    });
  }

  if (issue.issueType === "Permit Issue") {
    actions.push({
      id: `permit-status-${idx++}`,
      label: "Review Permit Status",
      description: "Check permit progress and address any holds.",
      priority: "medium",
      link: "/permitting",
      icon: "permit",
    });
  }

  if (job && issue.severity === "Urgent") {
    actions.push({
      id: `job-detail-${idx++}`,
      label: "Review Job Detail",
      description: "Check overall job status and coordinate resolution.",
      priority: "high",
      link: `/jobs/${job.id}`,
      icon: "issue",
    });
  }

  return actions.sort((a, b) => {
    const p = { high: 0, medium: 1, low: 2 };
    return p[a.priority] - p[b.priority];
  });
}

/** Get last activity info from a job */
export function getLastActivity(job: Job): { date: string; user: string; action: string } | null {
  if (!job.activityHistory || job.activityHistory.length === 0) return null;
  const sorted = [...job.activityHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return sorted[0];
}

/** Format relative time */
export function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}
